import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useTransactionalEmailService } from '../services/transactionalEmailService';
import { useTranslation } from './useTranslation';

export const useEmailVerification = () => {
    const navigate = useNavigate();
    const { verifyEmail } = useAuth();
    const [searchParams] = useSearchParams();
    const { showNotification } = useNotification();
    const emailService = useTransactionalEmailService();
    const verificationAttemptedRef = useRef(false);
    const { t } = useTranslation();

    useEffect(() => {
        const verifyEmailOobCode = searchParams.get('verifyEmailOobCode');
        if (verifyEmailOobCode && !verificationAttemptedRef.current) {
            // Set flag to prevent multiple verification attempts
            verificationAttemptedRef.current = true;

            // Process the verification code
            (async () => {
                try {
                    // Get email from URL if available
                    const email = searchParams.get('email');

                    // Call the API to verify the email
                    verifyEmail(verifyEmailOobCode)
                        .then(async () => {
                            // Show success notification
                            showNotification(t('auth.verifyEmail.successNotification'), 'success');
                            // If email is provided, send a welcome email
                            if (email) {
                                await emailService.sendWelcomeEmail(email);
                            }

                            // Refresh the entire page and redirect to home
                            const newUrl = `${window.location.origin}/`;
                            window.location.href = newUrl;
                        })
                        .catch((error) => {
                            console.error('Email verification failed:', error);
                            showNotification(
                                t('auth.verifyEmail.failedNotification'),
                                'error'
                            );
                        });
                } catch (error) {
                    console.error('Email verification failed:', error);
                    showNotification(t('auth.verifyEmail.failedNotification'), 'error');
                }
            })();
        }
    }, [searchParams, emailService, showNotification, verifyEmail, navigate, t]);
};
